import React from "react";
import {
	Badge,
	Box,
	Stack,
	Typography,
	styled,
	Avatar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateSelectedPendingContact } from "../../../state/actions/contacts";
import { SHIP_BADGE_ICON,SHORE_BADGE_ICON } from "../../../utils/constants";
 const Chip = ({
  label,
  variant = 'filled', 
  size = 'medium',
  color = 'default',
  clickable = false,
  deletable = false,
  disabled = false,
  avatar,
  icon,
  onClick,
  onDelete,
  className = '',
  sx = {}, 
  ...props
}) => {
  
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200 outline-none border-0 select-none";
  const sizeClasses = {
    small: "h-6 px-2 text-xs rounded-full",
    medium: "h-8 px-3 text-sm rounded-2xl", 
    large: "h-10 px-4 text-base rounded-2xl"
  };
  const filledColorClasses = {
    default: "bg-gray-200 text-gray-800",
    primary: "bg-blue-500 text-white",
    secondary: "bg-pink-500 text-white", 
    success: "bg-green-500 text-white",
    error: "bg-red-500 text-white",
    warning: "bg-orange-500 text-white"
  };
  const outlinedColorClasses = {
    default: "bg-transparent border border-gray-300 text-gray-700",
    primary: "bg-transparent border border-blue-500 text-blue-500",
    secondary: "bg-transparent border border-pink-500 text-pink-500",
    success: "bg-transparent border border-green-500 text-green-500", 
    error: "bg-transparent border border-red-500 text-red-500",
    warning: "bg-transparent border border-orange-500 text-orange-500"
  };
  const interactiveClasses = clickable && !disabled 
    ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:translate-y-0" 
    : "";
  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "";
  const colorClasses = variant === 'outlined' 
    ? outlinedColorClasses[color] 
    : filledColorClasses[color];
    
  const allClasses = `${baseClasses} ${sizeClasses[size]} ${colorClasses} ${interactiveClasses} ${disabledClasses} ${className}`.trim();

  const handleClick = (e) => {
    if (!disabled && clickable && onClick) {
      onClick(e);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!disabled && onDelete) {
      onDelete(e);
    }
  };

  const avatarSizeClasses = {
    small: "w-4 h-4",
    medium: "w-5 h-5", 
    large: "w-6 h-6"
  };

  const deleteSizeClasses = {
    small: "w-4 h-4 text-xs",
    medium: "w-5 h-5 text-sm",
    large: "w-6 h-6 text-base"
  };

  return (
    <div
      className={allClasses}
      onClick={handleClick}
      style={sx} 
      {...props}
    >
      {avatar && (
        <div className={`${avatarSizeClasses[size]} -ml-1 mr-2 rounded-full overflow-hidden flex items-center justify-center`}>
          {avatar}
        </div>
      )}
      
      {icon && (
        <div className={`${avatarSizeClasses[size]} -ml-1 mr-2 flex items-center justify-center`}>
          {icon}
        </div>
      )}
      
      <span className="truncate">{label}</span>
      
      {deletable && (
        <button
          className={`${deleteSizeClasses[size]} -mr-1 ml-1 rounded-full hover:bg-black hover:bg-opacity-10 flex items-center justify-center transition-colors duration-150 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={handleDelete}
          disabled={disabled}
          aria-label="Delete"
        >
          ×
        </button>
      )}
    </div>
  );
};
const StyledBadge = styled(Badge)({
	"& .MuiBadge-badge": {
		right: "4px", 
		bottom: "8px",
		transform: "scale(1) translate(50%, 50%)",
		width: "2rem",
		height: "2rem",
	},
});

const ContactListItemBox = styled(Stack)({
	padding: "1rem",
	cursor: "pointer",
	borderRadius: "8px",
});

const getBadgeIcon = (contactData) => {
	if (contactData?.contactType === "local") return null;
	let { rankLevel1 } = contactData;
	let rankLevel1SmallCase = (rankLevel1 || "").toLowerCase();

	let iconPath;
	switch (rankLevel1SmallCase) {
		case "shore":
			iconPath = SHIP_BADGE_ICON;
			break;
		case "ship":
			iconPath = SHORE_BADGE_ICON;
			break;
		default:
			iconPath = "";
	}
	return (
		iconPath && (
			<img
				src={iconPath}
				alt={`${rankLevel1SmallCase} badge`}
				style={{ width: "100%" }}
			/>
		)
	);
};

function ContactListItem(props) {
	const dispatch = useDispatch();
	const { contactData, selectedTabId } = props;
	const { selectedPendingContact, ignored: ignoredContacts } = useSelector(
		(state) => state.contacts
	);

	const setProfileImg = (userId) => {
		if (contactData.contactType === "local") {
			if (props.allProfileImages) {
				return props.allProfileImages[userId + contactData.userId];
			}
		} else {
			if (props.allProfileImages) {
				return props.allProfileImages[contactData.userId];
			}
		}
	};

	const isIgnoredContact = () => {
		let contactStatus = ignoredContacts.filter(
			(item) => item?.userId === contactData?.userId
		);
		return contactStatus.length > 0;
	};

	const handleSelectContact = async () => {
		await dispatch(updateSelectedPendingContact(contactData));
	};

	const getAvatarText = () => {
		let letters = contactData.userName
			? contactData.userName
					.split(" ")
					.map((n) => n[0])
					.join("")
			: "";
		return letters.slice(0, 2).toUpperCase();
	};

	let imgSrc = contactData && setProfileImg(contactData?.userId);

	if (selectedTabId === 1 && isIgnoredContact()) {
	  return null;
	}
  
	return (
		<ContactListItemBox
			flexDirection="row"
			alignItems="center"
			sx={{
				backgroundColor:
					selectedPendingContact.userId === contactData.userId
						? "#f3f3fa"
						: "#fff",
			}}
			justifyContent="space-between"
			onClick={() => handleSelectContact()}
		>
			<Stack flexDirection="row" sx={{ gap: 2 }} alignItems="center">
				<StyledBadge
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					badgeContent={getBadgeIcon(contactData)}
				>
					{imgSrc ? (
						<Avatar
							src={imgSrc}
							sx={{
								width: 40,
								height: 40,
								borderRadius: "50%",
							}}
						/>
					) : (
						<Avatar
							sx={{
								bgcolor: "#638dff",
								width: 40,
								height: 40,
								fontSize: "1rem",
								fontWeight: "300",
							}}
						>
							{getAvatarText()}
						</Avatar>
					)}
				</StyledBadge>
				<Typography>
					{contactData.name || contactData.userName || ""}
				</Typography>
			</Stack>
			{selectedTabId === 0 && (
				<Chip
					label="Request Sent"
					sx={{ backgroundColor: "#404585", color: "#fff" }}
				/>
			)}
			{selectedTabId === 1 && !isIgnoredContact() && (
				<Stack
					flexDirection="row"
					alignItems="center"
					gap={1}
					onClick={(e) => e.stopPropagation()}
				>
					<Chip
						label="Ignore"
						clickable={true}
						sx={{ backgroundColor: "#fff", color: "#727484" }}
						onClick={() => props.ignore(contactData)}
					/>
					<Chip
						label="Accept"
						clickable={true}
						sx={{ backgroundColor: "#376AFA", color: "#fff" }}
						onClick={() => props.accept(contactData)}
					/>
				</Stack>
			)}
			{isIgnoredContact() && (
				<Box onClick={(e) => e.stopPropagation()}>
					<Chip
						label="Accept"
						clickable={true}
						sx={{ backgroundColor: "#376AFA", color: "#fff" }}
						onClick={() => props.accept(contactData)}
					/>
				</Box>
			)}
		</ContactListItemBox>
	);
}

export default ContactListItem;
